import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { RsvpDetails, Settings } from "../models/api/rsvpApiTypes"
import { getGuestsByReservationCodeAPI } from "../api/rsvpApi"
import { toastWarning } from "../utils/toastMethod"
import { useAppSelector } from "../hook/useReduxHooks"
import { RootState } from "../redux/store"
import { CORE_STEP_NAMES, ERROR_TYPE, isCoreStep } from "../models/enum/rsvpEnum"
import StepRsvpBox from "./StepRsvp/StepRsvpBox"
import StepIntolerancesBox from "./StepIntolerances/StepIntolerancesBox"
import StepArrivalDateBox from "./StepArrivalDate/StepArrivalDateBox"
import StepBusBox from "./StepBus/StepBusBox"
import StepEmailBox from "./StepEmail/StepEmailBox"
import StepRecapBox from "./StepRecap/StepRecapBox"
import StepResultBox from "./StepResult/StepResultBox"
import DynamicStepBox from "./DynamicStep/DynamicStepBox"
import toasts from "../json/toast.json"
import { resolveTranslation } from "../utils/translations"

interface RsvpContentBodyProcedureProps {
  tenant: string
  reservationCode: string
  settings: Settings
}

const areStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index])

export default function RsvpContentBodyProcedure({ tenant, reservationCode, settings }: RsvpContentBodyProcedureProps) {
  const [step, setStep] = useState("")
  const [stepPipeline, setStepPipeline] = useState<string[]>([])
  const [rsvp, setRsvp] = useState<RsvpDetails>()
  const language = useAppSelector((state: RootState) => state.languageReducer.language)
  const navigate = useNavigate()

  const toastStrings = resolveTranslation(toasts.translations, language)

  const assembleStepPipeline = useCallback(
    (currentRsvp: RsvpDetails): string[] => {
      const shouldIncludeCoreStep = (stepName: string): boolean => {
        if (stepName === CORE_STEP_NAMES.RSVP) {
          return true
        }

        const guestFlagByStep: Record<
          string,
          keyof (typeof currentRsvp.invitation.guests)[number]["rsvpSettings"]
        > = {
          [CORE_STEP_NAMES.INTOLERANCES]: "showIntolerances",
          [CORE_STEP_NAMES.ARRIVAL_DATE]: "showArrivalDate",
          [CORE_STEP_NAMES.BUS]: "showBusTransferNeeded",
          [CORE_STEP_NAMES.EMAIL]: "showEmail",
        }

        const stepFlag = guestFlagByStep[stepName]
        if (!stepFlag || !currentRsvp.wedding.rsvpSettings[stepFlag]) {
          return false
        }

        // Important: only include if at least one *accepted* guest actually needs it
        return currentRsvp.invitation.guests.some((guest) => guest.rsvp.accepted && guest.rsvpSettings[stepFlag])
      }

      const pipeline = settings.steps
        .filter((stepItem) => stepItem.enabled)
        .filter(
          (stepItem) =>
            stepItem.name !== CORE_STEP_NAMES.PIN &&
            stepItem.name !== CORE_STEP_NAMES.SUMMARY &&
            stepItem.name !== CORE_STEP_NAMES.FINAL,
        )
        .sort((leftStep, rightStep) => {
          if (leftStep.order !== rightStep.order) {
            return leftStep.order - rightStep.order
          }

          const leftIsCore = isCoreStep(leftStep.name)
          const rightIsCore = isCoreStep(rightStep.name)

          if (leftIsCore === rightIsCore) {
            return 0
          }

          return leftIsCore ? -1 : 1
        })
        .filter((stepItem) => {
          if (isCoreStep(stepItem.name)) {
            return shouldIncludeCoreStep(stepItem.name)
          }

          if (stepItem.kind !== "Input") {
            return false
          }

          // Important: include only if at least one accepted guest is relevant for this extension step
          return currentRsvp.invitation.guests.some((guest) => {
            if (!guest.rsvp.accepted) {
              return false
            }

            if (guest.enabledSteps === undefined) {
              return true
            }

            return guest.enabledSteps.some((enabledStep) => enabledStep.name === stepItem.name && enabledStep.enabled)
          })
        })
        .map((stepItem) => stepItem.name)

      // Always add summary + final at the end
      return [...pipeline, CORE_STEP_NAMES.SUMMARY, CORE_STEP_NAMES.FINAL]
    },
    [settings.steps],
  )

  useEffect(() => {
    tenant &&
      reservationCode &&
      getGuestsByReservationCodeAPI(tenant, reservationCode)
        .then((result) => {
          const normalizedGuests = result.guests.map((guest) => {
            const accepted = guest.rsvp.accepted === null ? false : guest.rsvp.accepted

            // Keep existing behavior: if bus is enabled for a guest and is null, coerce to false
            const busTransferNeeded =
              guest.rsvpSettings.showBusTransferNeeded && guest.rsvp.busTransferNeeded === null
                ? false
                : guest.rsvp.busTransferNeeded

            const answers = guest.answers?.map((answer) => ({
              ...answer,
              textValue: answer.textValue === "" ? null : answer.textValue,
              dateValue: answer.dateValue === "" ? null : answer.dateValue,
            }))

            return {
              ...guest,
              answers,
              rsvp: {
                ...guest.rsvp,
                accepted,
                busTransferNeeded,
              },
            }
          })

          const newRsvp: RsvpDetails = {
            invitation: {
              ...result,
              guests: normalizedGuests,
            },
            wedding: settings,
          }

          const assembledStepPipeline = assembleStepPipeline(newRsvp)

          // If settings are misconfigured and nothing is enabled, bail out
          if (assembledStepPipeline.length === 2) {
            navigate("/error")
            return
          }

          setRsvp(newRsvp)
          setStepPipeline(assembledStepPipeline)
          setStep(assembledStepPipeline[0])
        })
        .catch((error) => {
          if (error.response?.status !== 200) {
            const errorMessage = error.response?.data?.Messages?.[0]
            switch (errorMessage) {
              case ERROR_TYPE.INVITATION_NOT_FOUND:
                toastWarning(toastStrings?.pinError !== undefined ? toastStrings?.pinError : "error")
                navigate({ pathname: `/confirm`, search: `?wedding=${tenant}` })
                break
              case ERROR_TYPE.RSVP_NOT_ENABLED:
                toastWarning(toastStrings?.procedureError !== undefined ? toastStrings?.procedureError : "error")
                navigate({ pathname: `/confirm`, search: `?wedding=${tenant}` })
                break
              default:
                navigate(`/error`)
                break
            }
          }
        })
  }, [navigate, reservationCode, tenant, settings, toastStrings?.pinError, toastStrings?.procedureError, assembleStepPipeline])

  // Rebuild pipeline when acceptance changes on the RSVP step
  useEffect(() => {
    if (!rsvp) return
    if (step !== CORE_STEP_NAMES.RSVP) return

    const rebuiltPipeline = assembleStepPipeline(rsvp)
    setStepPipeline((prev) => (areStringArraysEqual(prev, rebuiltPipeline) ? prev : rebuiltPipeline))
  }, [rsvp, step, assembleStepPipeline])

  return (
    <>
      {rsvp ? (
        <>
          {stepPipeline.map((stepName) => {
            if (stepName !== step) {
              return null
            }

            switch (stepName) {
              case CORE_STEP_NAMES.RSVP:
                return (
                  <StepRsvpBox
                    key={stepName}
                    settings={settings}
                    rsvp={rsvp}
                    setRsvp={setRsvp}
                    stepPipeline={stepPipeline}
                    setStep={setStep}
                  />
                )
              case CORE_STEP_NAMES.INTOLERANCES:
                return (
                  <StepIntolerancesBox
                    key={stepName}
                    settings={settings}
                    rsvp={rsvp}
                    setRsvp={setRsvp}
                    stepPipeline={stepPipeline}
                    setStep={setStep}
                  />
                )
              case CORE_STEP_NAMES.ARRIVAL_DATE:
                return (
                  <StepArrivalDateBox
                    key={stepName}
                    settings={settings}
                    rsvp={rsvp}
                    setRsvp={setRsvp}
                    stepPipeline={stepPipeline}
                    setStep={setStep}
                  />
                )
              case CORE_STEP_NAMES.BUS:
                return (
                  <StepBusBox
                    key={stepName}
                    settings={settings}
                    rsvp={rsvp}
                    setRsvp={setRsvp}
                    stepPipeline={stepPipeline}
                    setStep={setStep}
                  />
                )
              case CORE_STEP_NAMES.EMAIL:
                return (
                  <StepEmailBox
                    key={stepName}
                    settings={settings}
                    rsvp={rsvp}
                    setRsvp={setRsvp}
                    stepPipeline={stepPipeline}
                    setStep={setStep}
                  />
                )
              case CORE_STEP_NAMES.SUMMARY:
                return (
                  <StepRecapBox
                    key={stepName}
                    settings={settings}
                    tenant={tenant}
                    reservationCode={reservationCode}
                    rsvp={rsvp}
                    stepPipeline={stepPipeline}
                    setStep={setStep}
                  />
                )
              case CORE_STEP_NAMES.FINAL:
                return <StepResultBox key={stepName} settings={settings} tenant={tenant} reservationCode={reservationCode} />
              default:
                return (
                  <DynamicStepBox
                    key={stepName}
                    settings={settings}
                    stepName={stepName}
                    rsvp={rsvp}
                    setRsvp={setRsvp}
                    stepPipeline={stepPipeline}
                    setStep={setStep}
                  />
                )
            }
          })}
        </>
      ) : null}
    </>
  )
}
